import { Injectable } from '@angular/core';

// Narrow type for storage values
export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

@Injectable({ providedIn: 'root' })
export class LocalStorageService {
    private readonly storage: Storage | null;

    constructor() {
        this.storage = this.getStorageSafe();
    }

    // Basic API
    getItem<T extends string = string>(key: string): T | null {
        if (!this.storage) {
            return null;
        }
        return this.storage.getItem(key) as T | null;
    }

    setItem<T extends string = string>(key: string, value: T): void {
        if (!this.storage) {
            return;
        }
        try {
            this.storage.setItem(key, value);
        } catch {
            // ignore quota/security errors
        }
    }

    removeItem(key: string): void {
        if (!this.storage) {
            return;
        }
        try {
            this.storage.removeItem(key);
        } catch {
            /* ignore */
        }
    }

    clear(): void {
        if (!this.storage) {
            return;
        }
        try {
            this.storage.clear();
        } catch {
            /* ignore */
        }
    }

    hasKey(key: string): boolean {
        if (!this.storage) {
            return false;
        }
        try {
            return this.storage.getItem(key) !== null;
        } catch {
            return false;
        }
    }

    // JSON helpers
    setObject<T extends JsonValue>(key: string, value: T): void {
        if (!this.storage) {
            return;
        }
        try {
            this.storage.setItem(key, JSON.stringify(value));
        } catch {
            // ignore
        }
    }

    getObject<T extends JsonValue>(key: string): T | null {
        if (!this.storage) {
            return null;
        }
        const raw = this.storage.getItem(key);
        if (raw == null) {
            return null;
        }
        try {
            return JSON.parse(raw) as T;
        } catch {
            return null;
        }
    }

    private getStorageSafe(): Storage | null {
        try {
            if (typeof window !== 'undefined' && 'localStorage' in window) {
                return window.localStorage;
            }
        } catch {
            // SSR or restricted environment
        }
        return null;
    }
}
